import CustomForm from '../../lib/FormValidator/Form/CustomForm';
import { PlanillaPartido } from '../../system/PlanillaPartido/PlanillaPartido';
import { PlanillaDigitalAssertions } from '../../system/PlanillaPartido/PlanillaDigitalAssertions';
import { PlanillaPartidoJugador } from '../../system/PlanillaPartidoJugador/PlanillaPartidoJugador';
import { EventoXPlanilla } from '../../system/EventoXPlanilla/EventoXPlanilla';
import {
  DOS_MINUTOS,
  GOL,
  TARJETA_AMARILLA,
  TARJETA_AZUL,
  TARJETA_ROJA,
  TipoEventoPlanilla,
} from '../../system/TipoEventoPlanilla/TipoEventoPlanilla';
import { PlanillaPartidoOficial } from '../../system/PlanillaPartidoOficial/PlanillaPartidoOficial';
import PlanillaDigitalForm, { ROL_JUGADOR, ROL_OFICIAL, ROL_OFICIAL_AUXILIAR } from './PlanillaDigitalForm';

const CANT_MAX_DOS_MINUTOS_POR_PERSONA = 2;
const CANT_MAX_DOS_MINUTOS_POR_OFICIALES = 1;
const CANT_MAX_AMARILLAS_POR_EQUIPO_JUGADORES = 3;
const CANT_MAX_AMARILLAS_POR_EQUIPO_OFICIALES = 1;

class PlanillaDigitalFormEventMngr {
  protected constructor(private form: PlanillaDigitalForm, private refereeReview: boolean) {}
  public static build(form: PlanillaDigitalForm, refereeReview = false) {
    return new this(form, refereeReview);
  }

  // ---------------------------------- VALIDACION EVENTOS --------------------------------------

  getEquipoIdBy(id: number, rol: string) {
    let eqId: number | undefined = undefined;
    const planillaPartido = this.form.getPlanillaPartidoInput().getValue() as PlanillaPartido;
    if (rol === ROL_JUGADOR) {
      eqId = planillaPartido
        .getPlanillaPartidoJugadorList()
        .find((ppj) => +ppj.getId()! === +id)
        ?.getEqid();
    }
    if (rol === ROL_OFICIAL) {
      eqId = planillaPartido
        .getPlanillaPartidoOficialList()
        .find((ppo) => +ppo.getId()! === +id)
        ?.getEqid();
    }
    if (rol === ROL_OFICIAL_AUXILIAR) {
      eqId = planillaPartido
        .getPlanillaPartidoOficialAuxiliarList()
        .find((ppoa) => +ppoa.getId()! === +id)
        ?.getEqid();
    }
    return eqId;
  }

  getCantidadEventoXJugadorBy(id: number, rol: string, tipoEvento: string) {
    let evento = 0;

    if (rol === ROL_JUGADOR) {
      evento = this.form.getCantidadEventosXJugador(tipoEvento, +id);
    }

    if (rol === ROL_OFICIAL) {
      evento = this.form.getCantidadEventosXOficial(tipoEvento, +id);
    }

    if (rol === ROL_OFICIAL_AUXILIAR) {
      evento = this.form.getCantidadEventosXOficialAuxiliar(tipoEvento, +id);
    }
    return evento;
  }

  estaDescalificado(id: number, rol: string) {
    let rojas = this.getCantidadEventoXJugadorBy(id, rol, TARJETA_ROJA);
    let azules = this.getCantidadEventoXJugadorBy(id, rol, TARJETA_AZUL);

    return rojas > 0 || azules > 0;
  }

  tieneAmarilla(id: number, rol: string) {
    let amarillas = this.getCantidadEventoXJugadorBy(id, rol, TARJETA_AMARILLA);

    return amarillas > 0;
  }

  tieneRoja(id: number, rol: string) {
    let rojas = this.getCantidadEventoXJugadorBy(id, rol, TARJETA_ROJA);

    return rojas > 0;
  }

  tieneAzul(id: number, rol: string) {
    let azul = this.getCantidadEventoXJugadorBy(id, rol, TARJETA_AZUL);

    return azul > 0;
  }

  getDosMinutos(id: number, rol: string) {
    let dosMinutos = this.getCantidadEventoXJugadorBy(id, rol, DOS_MINUTOS);
    return dosMinutos;
  }

  validateGol(id: number, rol: string, tiempoActual: string, esDiferido: boolean) {
    if (rol === ROL_OFICIAL_AUXILIAR || rol === ROL_OFICIAL) {
      throw new ErrorEvent('no puede hacer un gol porque forma parte del cuerpo tecnico.');
    }

    if (!this.refereeReview && this.estaDescalificado(id, rol) && !esDiferido) {
      throw new ErrorEvent('se encuentra descalificado por lo tanto no es posible asignarle un nuevo gol.');
    } else if (!this.refereeReview && this.form.isJugadorExcluido(id, tiempoActual) && !esDiferido) {
      throw new ErrorEvent('se encuentra excluido por lo tanto no es posible asignarle un nuevo gol.');
    }
    return true;
  }

  validateDosMinutos(id: number, rol: string, esDiferido: boolean) {
    if (!this.refereeReview && this.estaDescalificado(id, rol) && !esDiferido) {
      throw new ErrorEvent(
        "se encuentra descalificado por lo tanto no es posible que reciba una exclusión por 2'."
      );
    }
    if (this.getDosMinutos(id, rol) === CANT_MAX_DOS_MINUTOS_POR_PERSONA) {
      throw new ErrorEvent(
        "ya tuvo dos exclusiones por 2' por lo tanto no es posible que reciba una nueva exclusión por 2'."
      );
    }

    const equipoId = this.getEquipoIdBy(id, rol);
    const dosMinutosEquipoOficiales = this.form.getCantidadDosMinutosOficiales(String(equipoId)!);

    if (
      dosMinutosEquipoOficiales === CANT_MAX_DOS_MINUTOS_POR_OFICIALES &&
      (rol === ROL_OFICIAL || rol === ROL_OFICIAL_AUXILIAR)
    ) {
      throw new ErrorEvent(
        "no puede recibir una nueva sanción por 2' porque el cuerpo tecnico ya fue sancionado por 2'."
      );
    }

    return true;
  }

  validateAmarilla(id: number, rol: string, esDiferido: boolean) {
    if (!this.refereeReview && this.estaDescalificado(id, rol) && !esDiferido) {
      throw new ErrorEvent(
        'se encuentra descalificado por lo tanto no es posible que reciba una tarjeta amarilla.'
      );
    }
    if (this.tieneAmarilla(id, rol)) {
      throw new ErrorEvent(
        'ya tuvo una tarjeta amarilla por lo tanto no es posible que reciba una nueva tarjeta amarilla.'
      );
    }
    if (!this.refereeReview && this.getDosMinutos(id, rol) > 0 && !esDiferido) {
      throw new ErrorEvent(
        "ya tuvo exclusiones por 2' por lo tanto no es posible que reciba una tarjeta amarilla."
      );
    }

    const equipoId = this.getEquipoIdBy(id, rol);
    const amarillasEquipoJugadores = this.form.getCantidadAmarillas(String(equipoId)!);

    const amarillasEquipoOficiales = this.form.getCantidadAmarillasOficiales(String(equipoId)!);

    if (amarillasEquipoJugadores === CANT_MAX_AMARILLAS_POR_EQUIPO_JUGADORES && rol === ROL_JUGADOR) {
      throw new ErrorEvent(
        'ya tiene 3 tarjetas amarillas por lo tanto no es posible que reciba una nueva tarjeta amarilla.',
        true
      );
    }

    if (
      amarillasEquipoOficiales === CANT_MAX_AMARILLAS_POR_EQUIPO_OFICIALES &&
      (rol === ROL_OFICIAL || rol === ROL_OFICIAL_AUXILIAR)
    ) {
      throw new ErrorEvent(
        'no puede recibir una nueva tarjeta amarilla porque el cuerpo tecnico ya fue sancionado con tarjeta amarilla.'
      );
    }

    return true;
  }

  validateRoja(id: number, rol: string, esDiferido: boolean) {
    if (!this.refereeReview && this.estaDescalificado(id, rol) && !esDiferido) {
      throw new ErrorEvent(
        'se encuentra descalificado por lo tanto no es posible que reciba una nueva tarjeta roja.'
      );
    }
    return true;
  }

  validateAzul(id: number, rol: string) {
    if (!this.tieneRoja(id, rol)) {
      throw new ErrorEvent('debe tener una tarjeta roja para que se lo sancione con una tarjeta azul.');
    }
    if (this.tieneAzul(id, rol)) {
      throw new ErrorEvent(
        'ya tiene una tarjeta azul por lo tanto no es posible que reciba una nueva tarjeta azul.'
      );
    }
    return true;
  }
}

export class ErrorEvent extends Error {
  constructor(message: string, private _isForTeam: boolean = false) {
    super(message);

    Object.setPrototypeOf(this, ErrorEvent.prototype);
  }

  isForTeam(): boolean {
    return !!this._isForTeam;
  }
}

export default PlanillaDigitalFormEventMngr;
